import { sha256 } from "@noble/hashes/sha2.js";
import { DRAND_QUICKNET_PUBLIC_KEY, publicKeyForChain } from "./chain.js";
import { bytesToHex, hexToBytes } from "./bytes.js";
import { deriveBackgammonDice, deriveDominoSeed, deriveMahjongSeed } from "./derive.js";
import type { DiceRollProof } from "./types.js";

/**
 * Verifier outcome shared by both backgammon (per-turn) and domino (per-hand)
 * verification flows. The modal renders rows from this in one of four states:
 *   - `verified` — BLS sig OK, derived value matches the recorded value.
 *   - `value-mismatch` — sig OK but derived value ≠ recorded value. The stored
 *     record was tampered with after the match.
 *   - `sig-invalid` — BLS verification failed (and/or the randomness ≠
 *     SHA256(signature)). The proof itself is forged or corrupt.
 *   - `unverified` — no proof attached (drand outage during the match). Modal
 *     renders the row as a yellow warning; the match summary stays "partial".
 */
export type ProofStatus = "verified" | "value-mismatch" | "sig-invalid" | "unverified";

function roundToBytes(round: number): Uint8Array {
  // drand's unchained scheme hashes the round as 8-byte big-endian unsigned.
  // Bun/Node bigint dance handles rounds > 2^32-1 cleanly (quicknet has only
  // crossed ~10^7 rounds so far but the spec is 64-bit).
  const out = new Uint8Array(8);
  let n = BigInt(round);
  for (let i = 7; i >= 0; i -= 1) {
    out[i] = Number(n & 0xffn);
    n >>= 8n;
  }
  return out;
}

/**
 * Confirm `randomness === SHA256(signature)`. drand's definition pins this
 * relationship, so any internally-inconsistent proof can be rejected
 * synchronously before paying the BLS curve cost.
 */
export function checkRandomness(proof: DiceRollProof): boolean {
  const sigBytes = hexToBytes(proof.signature);
  const recomputed = bytesToHex(sha256(sigBytes));
  return recomputed.toLowerCase() === proof.randomness.toLowerCase();
}

/**
 * Full BLS verification. Lazy-loads `@noble/curves/bls12-381` and pairs the
 * recorded signature against the pinned quicknet pubkey. Throws on malformed
 * input; returns boolean on a clean curve-level verdict.
 *
 * Lazy-import keeps the ~21 KB gzipped BLS module out of the main game bundle
 * — verifier modal is the only path that pulls it in.
 */
export async function verifyBlsSignature(
  proof: DiceRollProof,
  publicKeyHex: string = DRAND_QUICKNET_PUBLIC_KEY,
): Promise<boolean> {
  const { bls12_381 } = await import("@noble/curves/bls12-381.js");
  const bls = bls12_381.shortSignatures;
  const msg = sha256(roundToBytes(proof.round));
  const hashed = bls.hash(msg);
  return bls.verify(
    hexToBytes(proof.signature),
    hashed,
    hexToBytes(publicKeyHex),
  );
}

export interface DiceVerification {
  readonly status: ProofStatus;
  readonly derivedDice?: readonly [number, number];
}

export interface SeedVerification {
  readonly status: ProofStatus;
  readonly derivedSeed?: number;
}

/**
 * Verify one backgammon turn end-to-end.
 *
 * Chain-hash is looked up against `KNOWN_CHAINS`; an unrecognized chain (e.g. a
 * forged record citing a chain we never trusted, or a chain LoE rotated to but
 * we haven't shipped a registry entry for yet) returns `sig-invalid`. Once a
 * future chain rotation lands a new entry in `chain.ts`, historical proofs
 * against the old chain keep verifying against their archived pubkey here.
 */
export async function verifyDiceProof(
  proof: DiceRollProof,
  roomId: string,
  recordedDice: readonly [number, number],
): Promise<DiceVerification> {
  const publicKey = publicKeyForChain(proof.chainHash);
  if (publicKey === null) {
    return { status: "sig-invalid" };
  }
  if (!checkRandomness(proof)) {
    return { status: "sig-invalid" };
  }
  const derived = deriveBackgammonDice(proof.randomness, roomId, proof.counter);
  const derivedDice: [number, number] = [derived[0], derived[1]];
  const diceMatch = derived[0] === recordedDice[0] && derived[1] === recordedDice[1];
  if (!diceMatch) {
    return { status: "value-mismatch", derivedDice };
  }
  let blsOk: boolean;
  try {
    blsOk = await verifyBlsSignature(proof, publicKey);
  } catch {
    blsOk = false;
  }
  return { status: blsOk ? "verified" : "sig-invalid", derivedDice };
}

/**
 * How a game turns one drand round into its shuffle seed. The verifier is
 * otherwise identical across games — chain lookup, randomness check, derive,
 * compare, BLS — so the derivation is the only thing worth parameterizing.
 */
type SeedDerivation = (randomnessHex: string, roomId: string, counter: number) => number;

async function verifySeedProofWith(
  proof: DiceRollProof,
  roomId: string,
  recordedSeed: number,
  derive: SeedDerivation,
): Promise<SeedVerification> {
  const publicKey = publicKeyForChain(proof.chainHash);
  if (publicKey === null) {
    return { status: "sig-invalid" };
  }
  if (!checkRandomness(proof)) {
    return { status: "sig-invalid" };
  }
  const derivedSeed = derive(proof.randomness, roomId, proof.counter);
  if (derivedSeed !== recordedSeed) {
    return { status: "value-mismatch", derivedSeed };
  }
  let blsOk: boolean;
  try {
    blsOk = await verifyBlsSignature(proof, publicKey);
  } catch {
    blsOk = false;
  }
  return { status: blsOk ? "verified" : "sig-invalid", derivedSeed };
}

/**
 * Verify one domino hand end-to-end. Hand-level rather than turn-level because
 * the domino proof covers the whole shuffle — one drand round per hand.
 */
export async function verifySeedProof(
  proof: DiceRollProof,
  roomId: string,
  recordedSeed: number,
): Promise<SeedVerification> {
  return verifySeedProofWith(proof, roomId, recordedSeed, deriveDominoSeed);
}

/**
 * Verify one mahjong hand's wall seed end-to-end. Same shape as the domino
 * verifier above and deliberately a separate entry point rather than a flag on
 * it: the derivation tag differs (`"wall"` vs `"shuffle"`) and domino masks the
 * seed to 31 bits where mahjong keeps all 32, so a single function would have
 * to be told which game it is anyway — and `verifySeedProof`'s existing callers
 * must keep deriving exactly what they derive today.
 */
export async function verifyMahjongSeedProof(
  proof: DiceRollProof,
  roomId: string,
  recordedSeed: number,
): Promise<SeedVerification> {
  return verifySeedProofWith(proof, roomId, recordedSeed, deriveMahjongSeed);
}
